import { CountryCode } from '@/models/Currency';
import type { CsResolutionMethod, FulfillmentPurchasePresence } from '@/infrastructure/FulfillmentAdminClient';
import { isCsResolutionMethod } from '@/utils/fulfillmentCsResolution';

export type Band = 'wc' | 'marketing' | 'internal';

/**
 * Where an order came from, as one question. The id band (which lane minted the number) and the
 * channel column (which system sent it) are different axes, but on the live ledger they cut into
 * exactly these five populations — the other eleven pairings return nothing, whoever asks.
 */
export type OrderSource = 'all' | 'wc' | 'internal' | 'creatorhub' | 'marketingCsv';

export const ORDER_SOURCES: Array<{ value: OrderSource; label: string; band?: Band; channel?: string }> = [
  { value: 'all', label: 'All sources' },
  { value: 'wc', label: 'WooCommerce', band: 'wc' },
  // Internal clones carry channel WC, so only the band tells them apart from the order they came from.
  { value: 'internal', label: 'Internal', band: 'internal' },
  { value: 'creatorhub', label: 'CreatorHub', channel: 'CREATORHUB' },
  { value: 'marketingCsv', label: 'Marketing CSV', channel: 'MARKETING_CSV' },
];

const SOURCE_VALUES = new Set<string>(ORDER_SOURCES.map((source) => source.value));

/** An older link names the two axes it was built from; it still points at one of the five. */
const sourceOfBandAndChannel = (band: string, channel: string): OrderSource => {
  if (channel === 'CREATORHUB') {
    return 'creatorhub';
  }
  if (channel === 'MARKETING_CSV') {
    return 'marketingCsv';
  }
  if (band === 'internal') {
    return 'internal';
  }
  return band === 'wc' || channel === 'WC' ? 'wc' : 'all';
};

/** The live OMS list loads 100 by default; 20 made the operator click Load more to see one morning. */
export const PAGE_SIZES = [20, 50, 100, 200] as const;

export const DEFAULT_PAGE_SIZE = 100;

/** The countries the catalogue ships to; the picker offers exactly these, so a URL naming another is no filter. */
export const COUNTRY_CODES = Object.values(CountryCode);

export const FULFILLMENT_STATUSES = [
  'ORDERED',
  'AWAITING_INBOUND',
  'READY',
  'OUTBOUNDED',
  'CLOSED',
  'CANCELLED',
  'RETURNED',
  'RETURNED_PARTIAL',
  'MERGED',
  'RESOLVED',
] as const;

/** Server-stored CS states, independent of warehouse progress, WooCommerce and manual holds. */
export const CS_STATUS_FILTERS = [
  { value: 'processing', label: 'Processing' },
  { value: 'prepare-shipment', label: 'Prepare shipment' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'on-hold', label: 'On Hold' },
  { value: 'failed', label: 'Failed' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'canceled', label: 'Canceled' },
  { value: 'resolved', label: 'Resolved' },
] as const;

/** Either purchase axis: an empty value is no filter on it. */
export type PurchasePresenceFilter = FulfillmentPurchasePresence | '';

/**
 * The two purchase axes the list filters live JIT lines on. The labels carry V1's words, so an operator
 * looking for "Preparing" (order # present, tracking absent) or "NA" (order # absent) finds them.
 */
export const PURCHASE_ORDER_FILTERS: Array<{ value: PurchasePresenceFilter; label: string }> = [
  { value: '', label: 'Order #: any' },
  { value: 'PRESENT', label: 'Order #: present' },
  { value: 'ABSENT', label: 'Order #: absent (NA)' },
];

export const INBOUND_TRACKING_FILTERS: Array<{ value: PurchasePresenceFilter; label: string }> = [
  { value: '', label: 'Tracking: any' },
  { value: 'PRESENT', label: 'Tracking: present' },
  { value: 'ABSENT', label: 'Tracking: absent' },
];

export interface FulfillmentOrdersUrlState {
  source: OrderSource;
  fulfillmentStatus: string;
  csStatus: string;
  /** Only ever set alongside csStatus resolved: the method narrows resolved orders and nothing else. */
  csResolutionMethod: CsResolutionMethod | '';
  startDate: string;
  endDate: string;
  countryCode: string;
  pageSize: number;
  purchaseOrder: PurchasePresenceFilter;
  inboundTracking: PurchasePresenceFilter;
}

const STATUS_VALUES = new Set<string>(FULFILLMENT_STATUSES);
const CS_STATUS_VALUES = new Set<string>(CS_STATUS_FILTERS.map((entry) => entry.value));
const PAGE_SIZE_VALUES = new Set<number>(PAGE_SIZES);
const COUNTRY_CODE_VALUES = new Set<string>(COUNTRY_CODES);
const dateOnly = /^\d{4}-\d{2}-\d{2}$/;

/** A shared link is only as good as the day it names, so 2026-02-30 is dropped rather than sent to the API. */
const readDate = (value: string | null): string => {
  if (!value || !dateOnly.test(value)) {
    return '';
  }
  const date = new Date(`${value}T00:00:00.000Z`);
  return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value ? value : '';
};

const readCountryCode = (value: string | null): string => {
  const code = (value ?? '').trim().toUpperCase();
  return COUNTRY_CODE_VALUES.has(code) ? code : '';
};

const readPageSize = (value: string | null): number => {
  const size = Number(value);
  return PAGE_SIZE_VALUES.has(size) ? size : DEFAULT_PAGE_SIZE;
};

const readPresence = (value: string | null): PurchasePresenceFilter =>
  value === 'PRESENT' || value === 'ABSENT' ? value : '';

/** Anything the pickers cannot show is treated as absent: a stale link filters to nothing the operator can undo. */
export const readFulfillmentOrdersUrlState = (params: URLSearchParams): FulfillmentOrdersUrlState => {
  const source = params.get('source') ?? '';
  const fulfillmentStatus = params.get('fulfillmentStatus') ?? '';
  const csStatus = CS_STATUS_VALUES.has(params.get('csStatus') ?? '') ? params.get('csStatus')! : '';
  const csResolutionMethod = params.get('csResolutionMethod') ?? '';

  return {
    source: SOURCE_VALUES.has(source)
      ? (source as OrderSource)
      : sourceOfBandAndChannel(params.get('band') ?? '', params.get('channel') ?? ''),
    fulfillmentStatus: STATUS_VALUES.has(fulfillmentStatus) ? fulfillmentStatus : '',
    csStatus,
    csResolutionMethod: csStatus === 'resolved' && isCsResolutionMethod(csResolutionMethod) ? csResolutionMethod : '',
    startDate: readDate(params.get('startDate')),
    endDate: readDate(params.get('endDate')),
    countryCode: readCountryCode(params.get('countryCode')),
    pageSize: readPageSize(params.get('limit')),
    purchaseOrder: readPresence(params.get('purchaseOrder')),
    inboundTracking: readPresence(params.get('inboundTracking')),
  };
};

/** Only filters the operator actually set are written, so an untouched page keeps a clean URL. */
export const buildFulfillmentOrdersUrlParams = (state: FulfillmentOrdersUrlState): URLSearchParams => {
  const params = new URLSearchParams();

  if (state.source !== 'all') {
    params.set('source', state.source);
  }
  if (state.fulfillmentStatus) {
    params.set('fulfillmentStatus', state.fulfillmentStatus);
  }
  if (state.csStatus) {
    params.set('csStatus', state.csStatus);
  }
  if (state.csStatus === 'resolved' && state.csResolutionMethod) {
    params.set('csResolutionMethod', state.csResolutionMethod);
  }
  if (state.startDate) {
    params.set('startDate', state.startDate);
  }
  if (state.endDate) {
    params.set('endDate', state.endDate);
  }
  if (state.countryCode.trim()) {
    params.set('countryCode', state.countryCode.trim().toUpperCase());
  }
  if (state.pageSize !== DEFAULT_PAGE_SIZE) {
    params.set('limit', String(state.pageSize));
  }
  if (state.purchaseOrder) {
    params.set('purchaseOrder', state.purchaseOrder);
  }
  if (state.inboundTracking) {
    params.set('inboundTracking', state.inboundTracking);
  }

  return params;
};
